import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Plus, Trash2, Edit2, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { moneyAPI } from '../services/api';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const INCOME_CATS = [
  { value: 'collection',    label: 'Customer Collection', color: '#10b981' },
  { value: 'installation',  label: 'Installation Fee',    color: '#06b6d4' },
  { value: 'advance',       label: 'Advance Payment',     color: '#8b5cf6' },
  { value: 'reconnection',  label: 'Reconnection Fee',    color: '#f59e0b' },
  { value: 'other_income',  label: 'Other Income',        color: '#64748b' },
];

const EXPENSE_CATS = [
  { value: 'staff_salary',    label: 'Staff Salary',      color: '#ef4444' },
  { value: 'equipment',       label: 'Equipment',         color: '#f97316' },
  { value: 'maintenance',     label: 'Maintenance',       color: '#eab308' },
  { value: 'rent',            label: 'Rent',              color: '#7c3aed' },
  { value: 'utilities',       label: 'Utilities',         color: '#0ea5e9' },
  { value: 'transport',       label: 'Transport',         color: '#14b8a6' },
  { value: 'office',          label: 'Office Expenses',   color: '#6366f1' },
  { value: 'other_expense',   label: 'Other Expense',     color: '#94a3b8' },
];

const ALL_CATS = [...INCOME_CATS, ...EXPENSE_CATS];
const getCat = (v) => ALL_CATS.find(c => c.value === v) || { label: v, color: '#94a3b8' };

const PERIODS = [
  { value: 'week',  label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year',  label: 'This Year' },
  { value: 'all',   label: 'All Time' },
];

const METHODS = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];

function groupByDate(transactions) {
  const groups = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  transactions.forEach(t => {
    const d = new Date(t.date);
    let label;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    if (!groups[label]) groups[label] = [];
    groups[label].push(t);
  });
  return groups;
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function MoneyManagerPage() {
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('all');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { type } | txn object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        moneyAPI.getSummary(period),
        moneyAPI.getTransactions({ period, type: activeTab === 'all' ? undefined : activeTab }),
      ]);
      setSummary(s.data.data);
      setTransactions(t.data.data);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [period, activeTab]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await moneyAPI.deleteTransaction(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const periodLabel = PERIODS.find(p => p.value === period)?.label || '';

  const pieData = (summary?.categoryBreakdown || []).map(b => ({
    name: getCat(b._id.category).label,
    value: b.total,
    color: getCat(b._id.category).color,
    txnType: b._id.type,
  }));

  const grouped = groupByDate(transactions);

  const tabBtn = (v, l) => (
    <button onClick={() => setActiveTab(v)}
      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '.82rem', fontWeight: 600, transition: 'all .15s',
        background: activeTab === v ? '#fff' : 'transparent',
        color: activeTab === v ? 'var(--primary)' : 'var(--text-muted)',
        boxShadow: activeTab === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
      }}>
      {l}
    </button>
  );

  const periodBtn = (v, l) => (
    <button onClick={() => setPeriod(v)}
      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '.82rem', fontWeight: 600, transition: 'all .15s',
        background: period === v ? '#fff' : 'transparent',
        color: period === v ? 'var(--primary)' : 'var(--text-muted)',
        boxShadow: period === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
      }}>
      {l}
    </button>
  );

  return (
    <div>

      {/* ─── Summary Cards ─────────────────────────────────────────────────── */}
      <div className="summary-cards mb-5">

        {/* Balance */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)', borderRadius: 16, padding: '26px 28px', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Wallet size={18} color="#fff" />
            </div>
            <span style={{ fontSize: '.78rem', fontWeight: 600, opacity: .7, textTransform: 'uppercase', letterSpacing: '.08em' }}>Total Balance</span>
          </div>
          <div style={{ fontSize: '2.4rem', fontWeight: 900, letterSpacing: '-.02em', marginBottom: 4 }}>
            ₹{fmt(summary?.balance || 0)}
          </div>
          <div style={{ fontSize: '.75rem', opacity: .55 }}>All-time net (income − expense)</div>
        </div>

        {/* Income */}
        <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 16, padding: '26px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={17} color="#fff" />
            </div>
            <span style={{ fontSize: '.78rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase', letterSpacing: '.08em' }}>Income</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#15803d', letterSpacing: '-.02em', marginBottom: 4 }}>
            ₹{fmt(summary?.periodIncome || 0)}
          </div>
          <div style={{ fontSize: '.75rem', color: '#4ade80' }}>{periodLabel}</div>
        </div>

        {/* Expense */}
        <div style={{ background: '#fff1f2', border: '1.5px solid #fecdd3', borderRadius: 16, padding: '26px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingDown size={17} color="#fff" />
            </div>
            <span style={{ fontSize: '.78rem', fontWeight: 600, color: '#9f1239', textTransform: 'uppercase', letterSpacing: '.08em' }}>Expense</span>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#dc2626', letterSpacing: '-.02em', marginBottom: 4 }}>
            ₹{fmt(summary?.periodExpense || 0)}
          </div>
          <div style={{ fontSize: '.75rem', color: '#fca5a5' }}>{periodLabel}</div>
        </div>
      </div>

      {/* ─── Controls ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
          {PERIODS.map(p => periodBtn(p.value, p.label))}
        </div>
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
          {tabBtn('all', 'All')}
          {tabBtn('income', 'Income')}
          {tabBtn('expense', 'Expense')}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button className="btn btn-success" onClick={() => setModal({ type: 'income' })}>
            <Plus size={15} /> Add Income
          </button>
          <button className="btn btn-danger" onClick={() => setModal({ type: 'expense' })}>
            <Plus size={15} /> Add Expense
          </button>
        </div>
      </div>

      {/* ─── Content Grid ──────────────────────────────────────────────────── */}
      <div className="money-layout">

        {/* Left: Category Breakdown */}
        <div className="card">
          <div className="card-header"><h3>Category Breakdown</h3></div>
          <div style={{ padding: '16px 20px' }}>
            {pieData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
                No data for {periodLabel.toLowerCase()}
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={52} outerRadius={80}
                      dataKey="value" paddingAngle={3}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`₹${fmt(v)}`, '']} />
                  </PieChart>
                </ResponsiveContainer>

                <div style={{ marginTop: 4 }}>
                  {pieData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{d.name}</span>
                      </div>
                      <span style={{ fontSize: '.8rem', fontWeight: 700, color: d.txnType === 'income' ? '#15803d' : '#dc2626' }}>
                        {d.txnType === 'income' ? '+' : '−'}₹{fmt(d.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Transactions */}
        <div className="card">
          <div className="card-header">
            <h3>Transactions</h3>
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{transactions.length} entries</span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--text-muted)' }}>
              <Wallet size={44} style={{ marginBottom: 12, opacity: .2 }} />
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No transactions yet</p>
              <p style={{ fontSize: '.82rem' }}>Add your first income or expense above</p>
            </div>
          ) : (
            Object.entries(grouped).map(([dateLabel, txns]) => {
              const dayIncome  = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
              const dayExpense = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
              return (
                <div key={dateLabel}>
                  {/* Date group header */}
                  <div style={{ padding: '9px 20px', background: '#f8fafc', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      {dateLabel}
                    </span>
                    <div style={{ display: 'flex', gap: 12, fontSize: '.75rem', fontWeight: 700 }}>
                      {dayIncome  > 0 && <span style={{ color: '#15803d' }}>+₹{fmt(dayIncome)}</span>}
                      {dayExpense > 0 && <span style={{ color: '#dc2626' }}>−₹{fmt(dayExpense)}</span>}
                    </div>
                  </div>

                  {/* Individual transactions */}
                  {txns.map(txn => {
                    const cat = getCat(txn.category);
                    return (
                      <div key={txn._id}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: '1px solid var(--border)', transition: 'background .13s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>

                        {/* Category indicator */}
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: cat.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: cat.color }} />
                        </div>

                        {/* Details */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {txn.description}
                          </div>
                          <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {cat.label}
                            {' · '}
                            <span style={{ textTransform: 'capitalize' }}>{txn.paymentMethod?.replace('_', ' ')}</span>
                            {txn.reference && <span style={{ fontStyle: 'italic' }}> · {txn.reference}</span>}
                          </div>
                        </div>

                        {/* Amount + time */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: '1rem', color: txn.type === 'income' ? '#15803d' : '#dc2626' }}>
                            {txn.type === 'income' ? '+' : '−'}₹{fmt(txn.amount)}
                          </div>
                          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            {new Date(txn.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>

                        {/* Edit / Delete */}
                        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                          <button onClick={() => setModal(txn)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 5, borderRadius: 5 }}
                            title="Edit">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => handleDelete(txn._id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 5, borderRadius: 5 }}
                            title="Delete">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Modal ─────────────────────────────────────────────────────────── */}
      {modal && (
        <TransactionModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

/* ─── Add / Edit Modal ───────────────────────────────────────────────────────── */
function TransactionModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?._id;

  const [form, setForm] = useState({
    type:          initial?.type          || 'income',
    amount:        initial?.amount        ? String(initial.amount) : '',
    category:      initial?.category      || '',
    description:   initial?.description   || '',
    date:          initial?.date
                     ? new Date(initial.date).toISOString().slice(0, 10)
                     : new Date().toISOString().slice(0, 10),
    paymentMethod: initial?.paymentMethod || 'cash',
    reference:     initial?.reference     || '',
  });
  const [loading, setLoading] = useState(false);

  const cats = form.type === 'income' ? INCOME_CATS : EXPENSE_CATS;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parseFloat(form.amount) > 0) { toast.error('Enter a valid amount'); return; }
    if (!form.category) { toast.error('Select a category'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await moneyAPI.updateTransaction(initial._id, form);
        toast.success('Transaction updated');
      } else {
        await moneyAPI.createTransaction(form);
        toast.success(form.type === 'income' ? 'Income added' : 'Expense added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%', padding: '9px 12px',
    border: '1px solid var(--border)', borderRadius: 8,
    fontSize: '.88rem', boxSizing: 'border-box', fontFamily: 'inherit',
    outline: 'none',
  };

  const isIncome = form.type === 'income';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Transaction' : `Add ${isIncome ? 'Income' : 'Expense'}`}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Type toggle — only for new */}
            {!isEdit && (
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 4 }}>
                {['income', 'expense'].map(t => (
                  <button key={t} type="button"
                    onClick={() => setForm(f => ({ ...f, type: t, category: '' }))}
                    style={{ flex: 1, padding: '10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: '.88rem', transition: 'all .15s',
                      background: form.type === t ? (t === 'income' ? '#10b981' : '#ef4444') : 'transparent',
                      color: form.type === t ? '#fff' : 'var(--text-muted)',
                    }}>
                    {t === 'income' ? '↑ Income' : '↓ Expense'}
                  </button>
                ))}
              </div>
            )}

            {/* Amount */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Amount (₹) *</label>
              <input style={{ ...inp, fontSize: '1.5rem', fontWeight: 900, padding: '14px 16px',
                color: isIncome ? '#15803d' : '#dc2626',
                borderColor: isIncome ? '#bbf7d0' : '#fecdd3',
                background: isIncome ? '#f0fdf4' : '#fff1f2',
              }}
                type="number" step="0.01" min="0.01" required placeholder="0.00"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                autoFocus={!isEdit}
              />
            </div>

            {/* Category grid */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 8 }}>Category *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {cats.map(c => (
                  <button key={c.value} type="button"
                    onClick={() => set('category', c.value)}
                    style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                      border: `1.5px solid ${form.category === c.value ? c.color : 'var(--border)'}`,
                      background: form.category === c.value ? c.color + '18' : '#fff',
                      color: form.category === c.value ? c.color : 'var(--text)',
                      fontSize: '.78rem', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                      transition: 'all .13s',
                    }}>
                    <div style={{ width: 9, height: 9, borderRadius: 3, background: c.color, flexShrink: 0 }} />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Description *</label>
              <input style={inp} required placeholder="What is this transaction for?"
                value={form.description} onChange={e => set('description', e.target.value)} />
            </div>

            {/* Date + Method */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Date *</label>
                <input style={inp} type="date" required value={form.date} onChange={e => set('date', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payment Method</label>
                <select style={inp} value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                  {METHODS.map(m => (
                    <option key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reference */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Reference / Note (optional)</label>
              <input style={inp} placeholder="Invoice no, receipt no, remarks..."
                value={form.reference} onChange={e => set('reference', e.target.value)} />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}
              className={`btn ${isIncome ? 'btn-success' : 'btn-danger'}`}>
              {loading ? 'Saving...' : isEdit ? 'Update Transaction' : `Add ${isIncome ? 'Income' : 'Expense'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
